// ==============================================
// SEMANA DE AVALIAÇÕES - PORTAL DNA CONNECT
// Visualização Premium para Famílias (GRADE)
// ==============================================

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar, BookOpen, Clock, Download, Users, 
  ChevronRight, FileText, Printer, CheckCircle2,
  AlertCircle, Info
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = window.location.origin || '';

// ==============================================
// CORES DAS DISCIPLINAS
// ==============================================
const CORES_DISCIPLINAS = {
  'Matemática': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  'Português': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'Língua Portuguesa': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'História': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  'Geografia': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  'Ciências': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  'Física': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'Química': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'Biologia': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  'Inglês': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  'Espanhol': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200' },
  'Artes': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-200' },
  'Arte': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-200' },
  'Educação Física': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200' },
  'Filosofia': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
  'Sociologia': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-200' },
  'Programa Bilíngue': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  'Produção Textual': { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-200' },
  'Educação Financeira': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
};

const getCorDisciplina = (nome) => {
  return CORES_DISCIPLINAS[nome] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
};

// ==============================================
// COMPONENTE DE GRADE PREMIUM PARA FAMÍLIAS
// ==============================================
const GradeAvaliacoesConnect = ({ semana }) => {
  const [viewMode, setViewMode] = useState('grade'); // 'grade' ou 'lista'

  // Organizar dias da semana
  const diasSemana = useMemo(() => {
    const dias = {};
    if (!semana.avaliacoes_por_data) return dias;
    
    Object.keys(semana.avaliacoes_por_data).sort().forEach(data => {
      const d = new Date(data + 'T12:00:00');
      const diaSemana = d.toLocaleDateString('pt-BR', { weekday: 'long' });
      const diaFormatado = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      dias[data] = { 
        diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
        diaFormatado,
        date: d
      };
    });
    return dias;
  }, [semana]);

  // Obter horários únicos
  const horariosUnicos = useMemo(() => {
    const horarios = new Set();
    if (!semana.avaliacoes_por_data) return [];
    
    Object.values(semana.avaliacoes_por_data).forEach(avaliacoes => {
      avaliacoes.forEach(aval => {
        if (aval.horario) {
          horarios.add(aval.horario);
        }
      });
    });
    return Array.from(horarios).sort();
  }, [semana]);

  // Montar grid
  const grid = useMemo(() => {
    const g = {};
    horariosUnicos.forEach(horario => {
      g[horario] = {};
      Object.keys(diasSemana).forEach(data => {
        g[horario][data] = null;
      });
    });

    if (semana.avaliacoes_por_data) {
      Object.entries(semana.avaliacoes_por_data).forEach(([data, avaliacoes]) => {
        avaliacoes.forEach(aval => {
          if (g[aval.horario] && g[aval.horario].hasOwnProperty(data)) {
            g[aval.horario][data] = aval;
          }
        });
      });
    }

    return g;
  }, [semana, horariosUnicos, diasSemana]);

  const CORES_HEADER = [
    'bg-gradient-to-r from-orange-500 to-orange-400',
    'bg-gradient-to-r from-pink-500 to-pink-400',
    'bg-gradient-to-r from-green-500 to-green-400',
    'bg-gradient-to-r from-amber-500 to-amber-400',
    'bg-gradient-to-r from-cyan-500 to-cyan-400',
  ];

  const todasAvaliacoes = useMemo(() => {
    if (!semana.avaliacoes_por_data) return [];
    return Object.entries(semana.avaliacoes_por_data)
      .flatMap(([data, avaliacoes]) => 
        avaliacoes.map(a => ({ ...a, data }))
      )
      .sort((a, b) => new Date(a.data) - new Date(b.data));
  }, [semana]);

  return (
    <div className="space-y-4">
      {/* Botões de Visualização */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grade' ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode('grade')}
            className={viewMode === 'grade' ? "bg-purple-600" : ""}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Grade Visual
          </Button>
          <Button
            variant={viewMode === 'lista' ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode('lista')}
            className={viewMode === 'lista' ? "bg-purple-600" : ""}
          >
            <FileText className="h-4 w-4 mr-1" />
            Lista
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {viewMode === 'grade' && (
        <Card className="overflow-hidden shadow-xl print:shadow-none">
          {/* Header Premium */}
          <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white p-6 text-center print:bg-purple-600">
            <h2 className="text-2xl font-bold tracking-wide">{semana.turma}</h2>
            <p className="text-purple-200 mt-1">
              Semana de Avaliações - {semana.trimestre}º Trimestre {semana.ano}
            </p>
            <p className="text-purple-300 text-sm mt-1">{semana.periodo}</p>
          </div>

          {/* Tabela Grade */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="bg-gray-100 text-gray-700 font-semibold py-3 px-4 text-left w-32">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horário
                    </div>
                  </th>
                  {Object.entries(diasSemana).map(([data, info], idx) => (
                    <th 
                      key={data}
                      className={`${CORES_HEADER[idx % CORES_HEADER.length]} text-white font-semibold py-3 px-4 text-center min-w-[140px]`}
                    >
                      <div>{info.diaSemana}</div>
                      <div className="text-xs font-normal opacity-90">{info.diaFormatado}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {horariosUnicos.map((horario, rowIdx) => {
                  const [aulaInfo, horarioRange] = horario.includes(' - ') 
                    ? ['', horario] 
                    : [horario.split(' ')[0], horario.split(' ').slice(-1)[0]];
                  
                  return (
                    <tr key={horario} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-4 font-medium text-gray-600 border-r border-gray-200">
                        <div className="text-xs text-gray-500">{aulaInfo}</div>
                        <div className="text-sm font-mono">{horarioRange}</div>
                      </td>
                      {Object.keys(diasSemana).map(data => {
                        const aval = grid[horario]?.[data];
                        const cor = aval ? getCorDisciplina(aval.disciplina) : null;
                        
                        return (
                          <td key={data} className="p-2 border border-gray-100">
                            {aval ? (
                              <div 
                                className={`${cor.bg} ${cor.border} border rounded-lg p-3 text-center transition-all hover:scale-[1.02] hover:shadow-md`}
                              >
                                <p className={`font-semibold ${cor.text}`}>{aval.disciplina}</p>
                                <p className="text-xs text-gray-500 mt-1">{aval.tipo || 'Prova'}</p>
                                {aval.observacoes && (
                                  <p className="text-xs text-gray-400 mt-1 truncate" title={aval.observacoes}>
                                    {aval.observacoes}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="h-full min-h-[70px] flex items-center justify-center text-gray-300">
                                —
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 border-t">
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
              <div className="text-gray-500">
                Total: <strong className="text-purple-700">{semana.total_avaliacoes}</strong> avaliações
              </div>
              <div className="text-gray-400 text-xs">
                Publicado em: {new Date(semana.publicado_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
              </div>
            </div>
          </div>
        </Card>
      )}

      {viewMode === 'lista' && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-700 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {semana.turma} - Lista de Avaliações
            </CardTitle>
            <CardDescription className="text-purple-200">
              {semana.total_avaliacoes} avaliações programadas • {semana.trimestre}º Trimestre
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid gap-3">
              {todasAvaliacoes.map((aval, idx) => {
                const cor = getCorDisciplina(aval.disciplina);
                const dataObj = new Date(aval.data + 'T12:00:00');
                
                return (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-4 ${cor.bg} ${cor.border} border rounded-lg hover:shadow-md transition-all`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[70px] bg-white rounded-lg p-2 shadow-sm">
                        <p className="text-xs text-gray-500">
                          {dataObj.toLocaleDateString('pt-BR', { weekday: 'short' })}
                        </p>
                        <p className="font-bold text-lg">{dataObj.getDate()}</p>
                        <p className="text-xs text-gray-500">
                          {aval.horario?.split(' - ')[0] || aval.horario}
                        </p>
                      </div>
                      <div>
                        <p className={`font-semibold text-lg ${cor.text}`}>{aval.disciplina}</p>
                        <p className="text-sm text-gray-600">{aval.tipo || 'Prova'}</p>
                        {aval.observacoes && (
                          <p className="text-xs text-gray-500 mt-1">{aval.observacoes}</p>
                        )}
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Confirmada
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
export default function SemanaAvaliacoesConnect({ token, alunoId }) {
  const [loading, setLoading] = useState(true);
  const [semanas, setSemanas] = useState([]);
  const [semanaAtiva, setSemanaAtiva] = useState(null);

  useEffect(() => {
    const fetchSemanas = async () => {
      try {
        setLoading(true);
        let url = `${API_URL}/api/assessment-week/connect/semana-avaliacoes?token=${token}`;
        if (alunoId) {
          url += `&aluno_id=${alunoId}`;
        }
        
        const res = await fetch(url);
        const data = await res.json();
        
        setSemanas(data.semanas || []);
        if (data.semanas?.length > 0) {
          setSemanaAtiva(data.semanas[0]);
        }
      } catch (error) {
        console.error('Erro ao carregar semanas:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSemanas();
    }
  }, [token, alunoId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (semanas.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Nenhuma Semana de Avaliações Publicada
          </h3>
          <p className="text-gray-500">
            Quando a escola publicar o calendário de provas, ele aparecerá aqui.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Calendar className="h-8 w-8" />
          <h2 className="text-2xl font-bold">Semana de Avaliações Trimestrais</h2>
        </div>
        <p className="text-purple-200">
          Calendário oficial das avaliações da turma
        </p>
      </div>

      {/* Seletor de Semana (se houver mais de uma) */}
      {semanas.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {semanas.map((semana) => (
            <Button
              key={semana.id}
              variant={semanaAtiva?.id === semana.id ? "default" : "outline"}
              onClick={() => setSemanaAtiva(semana)}
              className={`flex-shrink-0 ${
                semanaAtiva?.id === semana.id 
                  ? 'bg-purple-600 text-white' 
                  : ''
              }`}
            >
              {semana.trimestre}º Tri - {semana.turma}
            </Button>
          ))}
        </div>
      )}

      {semanaAtiva && (
        <>
          {/* Info Card */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-purple-600" />
                  <span className="font-medium">{semanaAtiva.turma}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  <span>{semanaAtiva.trimestre}º Trimestre {semanaAtiva.ano}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span>{semanaAtiva.periodo}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mensagem da Escola */}
          {semanaAtiva.mensagem && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-blue-800">{semanaAtiva.mensagem}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Grade de Avaliações Premium */}
          <GradeAvaliacoesConnect semana={semanaAtiva} />
        </>
      )}
    </div>
  );
}
